
import PageContent from "~/components/categories/PageContent";
import { createCryptoService } from "~/utils/crypto";
import { apiContentError } from "~/utils/api-content";

const technicalErrorId = "85c8ffed-a13c-466e-ba1b-f5811c47dd90";

const cryptoService = createCryptoService(process.env.baseEncryptionKey, process.env.baseEncryptionIv);

function trackPageLoadError(error) {
    if (typeof window === "undefined") return;
    if (!process.client) return;

    const trackingDetails = {
        error_id: technicalErrorId,
        timestamp: new Date().toISOString(),
        request_status: error?.response?.status || "unknown",
        request_path: error?.config?.url || "unknown",
        error_message: error?.message || "unknown",
    };

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
        ...trackingDetails,
        event: "technical_error",
    });

    window.InsiderQueue = window.InsiderQueue || [];
    window.InsiderQueue.length = 0;

    window.InsiderQueue.push({
        type: "custom_event",
        value: [
            {
                event_name: "technical_error",
                event_parameters: {
                    custom: trackingDetails,
                },
            },
        ],
    });

    window.InsiderQueue.push({
        type: "init",
    });
}

function getPageContentPayload(decrypted) {
    return {
        hero: decrypted?.hero || null,
        product_categories: decrypted?.product_categories || null,
        ads: decrypted?.ads || null,
    };
}

async function getPageContent($axios, url) {
    const relativeUrl = url.replace(/^\//, "");
    let response;

    try {
        response = await $axios.get(relativeUrl, { timeout: 60000 });
    } catch (error) {
        const currentBaseUrl = $axios.defaults.baseURL || "";
        const isHttpsServerRequest = !process.client && /^https:/i.test(currentBaseUrl);
        if (isHttpsServerRequest) {
            console.warn(`[getPageContent] HTTPS request failed on SSR (${error.message}). Retrying over HTTP...`);
            const httpBaseUrl = currentBaseUrl.replace(/^https:/i, "http:");
            const fallbackUrl = `${httpBaseUrl.replace(/\/+$/, "")}/${relativeUrl}`;
            response = await $axios.get(fallbackUrl, { timeout: 120000 });
        } else {
            throw error;
        }
    }

    const encrypted = response?.data?.data?.attributes?.encrypted;

    if (!encrypted) {
        throw apiContentError("Products response is missing its encrypted payload.", response);
    }

    let decrypted;

    try {
        decrypted = cryptoService.decrypt(encrypted);
    } catch (_) {
        throw apiContentError("Products response could not be decrypted.", response);
    }

    if (!decrypted) {
        throw apiContentError("Products content is unavailable.", response);
    }

    if (!Array.isArray(decrypted.product_categories)) {
        throw apiContentError("Products response is missing its categories.", response);
    }

    return getPageContentPayload(decrypted);
}

export default {
    components: {
        PageContent,
    },

    async asyncData({ $axios, store, error }) {
        if (process.client) {
            store.commit("global/loader/toggleLoaderStatus", {
                status: true,
            });
        }

        try {
            const pageContentPayload = await getPageContent($axios, "web/page/products");

            return {
                pageContentPayload,
            };
        } catch (fetchError) {
            console.error("Unable to load products:", {
                code: fetchError.code || "UNKNOWN",
                status: fetchError.response?.status || "no response",
            });

            // Only track analytics on client side
            if (process.client) {
                trackPageLoadError(fetchError);
            }

            // Pass actual API error code
            const statusCode = fetchError.response?.status || 500;

            return error({
                statusCode,
                message: "Unable to load products.",
            });
        } finally {
            if (process.client) {
                store.commit("global/loader/toggleLoaderStatus", {
                    status: false,
                });
            }
        }
    },

    mounted() {
        this.scrollToTop();

        setTimeout(() => {
            if (this.pageContentPayload.ads?.length) {
                this.$store.commit("global/dialog/populateContents", {
                    items: this.pageContentPayload.ads,
                    type: "custom-no-title",
                });
            }
        }, 600);
    },

    head() {
        const title = "Insurance Products | Malayan Insurance (Philippines)";

        const description = "Explore Malayan Insurance products for travel, vehicles, property, personal protection, and more. Find coverage that fits your needs.";

        const canonicalUrl = `https://malayan.com${this.$route.path}`;

        const imageUrl = "https://malayan.com/mico2022-[light].webp";

        const firstSlide = Array.isArray(this.pageContentPayload?.hero) ? this.pageContentPayload.hero[0] : null;

        const heroImages = Array.isArray(firstSlide?.images) ? firstSlide.images : [];

        const mobileHeroSrc = heroImages.find((image) => image?.category === "image_mobile" && image?.path)?.path || heroImages.find((image) => image?.path)?.path || null;

        const preloadLinks = mobileHeroSrc
            ? [
                  {
                      hid: "hero-preload",
                      rel: "preload",
                      as: "image",
                      href: mobileHeroSrc,
                      media: "(max-width: 1024px)",
                  },
              ]
            : [];

        return {
            title,

            link: [
                {
                    hid: "canonical",
                    rel: "canonical",
                    href: canonicalUrl,
                },
                ...preloadLinks,
            ],

            meta: [
                {
                    hid: "description",
                    name: "description",
                    content: description,
                },

                // Twitter
                {
                    hid: "twitter:card",
                    name: "twitter:card",
                    content: "summary_large_image",
                },
                {
                    hid: "twitter:title",
                    name: "twitter:title",
                    content: title,
                },
                {
                    hid: "twitter:description",
                    name: "twitter:description",
                    content: description,
                },
                {
                    hid: "twitter:image",
                    name: "twitter:image",
                    content: imageUrl,
                },

                // Open Graph
                {
                    hid: "og:title",
                    property: "og:title",
                    content: title,
                },
                {
                    hid: "og:description",
                    property: "og:description",
                    content: description,
                },
                {
                    hid: "og:url",
                    property: "og:url",
                    content: canonicalUrl,
                },
                {
                    hid: "og:image",
                    property: "og:image",
                    content: imageUrl,
                },
                {
                    hid: "og:image:alt",
                    property: "og:image:alt",
                    content: "Malayan Insurance Products",
                },
                {
                    hid: "og:site_name",
                    property: "og:site_name",
                    content: "Malayan Insurance (Philippines)",
                },
                {
                    hid: "og:type",
                    property: "og:type",
                    content: "website",
                },
            ],
        };
    },
};
